import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "../lib/cn";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-40 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary,theme(colors.blue.600))] focus-visible:ring-offset-1",
  {
    variants: {
      variant: {
        // Canonical variants
        primary:
          "bg-[var(--color-primary,theme(colors.blue.700))] text-white border border-transparent hover:opacity-90",
        secondary:
          "bg-transparent border border-[var(--color-border,theme(colors.slate.600))] text-[var(--color-text,theme(colors.slate.100))] hover:bg-white/10",
        ghost:
          "bg-transparent border border-transparent text-[var(--color-text,theme(colors.slate.100))] hover:bg-white/10",
        destructive: "bg-red-700 text-white border border-transparent hover:bg-red-600",
        // Aliases for backward compatibility with shadcn variant names
        default:
          "bg-[var(--color-primary,theme(colors.blue.700))] text-white border border-transparent hover:opacity-90",
        outline:
          "bg-transparent border border-[var(--color-border,theme(colors.slate.600))] text-[var(--color-text,theme(colors.slate.100))] hover:bg-white/10",
        link: "bg-transparent border-transparent text-[var(--color-primary,theme(colors.blue.600))] underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 px-3 text-xs",
        lg: "h-10 px-6",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ComponentProps<"button">, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export function Button({ className, variant, size, asChild = false, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { buttonVariants };
